import React, { type ReactElement, type ReactNode } from 'react';

import type { SelectOption } from '../../../select_field/types';
import {
  FormContextConsumer,
  type FormContextType,
} from '../../form_context/form_context';
import formField, { pickProps } from '../../form_field/form_field';
import type { FormFieldEvent } from '../../types';
import {
  SelectFieldBlur,
  type SelectFieldWrapperProps,
  selectComponentProps,
} from '../select/select';

export type SelectFieldMultiWrapperProps<
  V = string,
  O = SelectOption,
  IO extends SelectOption = SelectOption,
> = Omit<SelectFieldWrapperProps<V, O, IO>, 'onChange' | 'value'> & {
  // With plainMultiValues the array holds bare option ids, so both shapes are
  // valid, and a value being resolved can hold a mix of the two.
  value?: (V | SelectOption | string)[] | undefined;
  // Always an array, so it can't be undefined. It carries the same two shapes
  // the value does: bare ids with plainMultiValues, whole options otherwise.
  onChange?: (value: (V | SelectOption)[], event?: FormFieldEvent) => void;
};

// The same bridge as in field_wrappers/select/select.tsx, for the multi props.
// biome-ignore lint/plugin: see above.
const Field = formField(SelectFieldBlur, {
  defaultValue: [],
  transformProps: (props) => pickProps(props, selectComponentProps),
}) as <V, O, IO extends SelectOption>(
  props: SelectFieldMultiWrapperProps<V, O, IO> & FormContextType,
) => ReactElement;

class SelectFieldMultiWrapper<
  V = string,
  O = SelectOption,
  IO extends SelectOption = SelectOption,
> extends React.Component<SelectFieldMultiWrapperProps<V, O, IO>> {
  render(): ReactNode {
    return (
      <FormContextConsumer>
        {(context) => <Field {...this.props} isMulti {...context} />}
      </FormContextConsumer>
    );
  }
}

export default SelectFieldMultiWrapper;
